import { useState, useEffect } from 'react';
import { notificationAPI, announcementAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Bell,
  Loader2,
  Mail,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Clock,
  ExternalLink,
  Megaphone
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function Notifications() {
  const [logs, setLogs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [logsRes, annRes] = await Promise.all([
        notificationAPI.getLogs(),
        announcementAPI.getActive()
      ]);
      setLogs(logsRes.data || []);
      setAnnouncements(annRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email':
        return <Mail size={16} className="text-[#1877F2]" />;
      case 'telegram':
        return <MessageSquare size={16} className="text-[#1877F2]" />;
      default:
        return <Bell size={16} className="text-[#1877F2]" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#EBF5ED] text-[#1E7132]">
            <CheckCircle2 size={12} />
            Đã gửi
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FCE9EC] text-[#A8112A]">
            <XCircle size={12} />
            Thất bại
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FEF9E6] text-[#8F6702]">
            <Clock size={12} />
            Đang chờ
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin text-[#1877F2]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="animate-fadeIn">
        <h1 className="text-2xl font-bold text-[#1C1E21] flex items-center gap-2">
          <Bell className="text-[#1877F2]" />
          Lịch sử thông báo
        </h1>
        <p className="text-[#606770] mt-1">
          Danh sách các thông báo đã gửi qua Email và Telegram
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="announcements" className="w-full animate-fadeIn stagger-1">
        <TabsList className="mb-6 bg-white border">
          <TabsTrigger value="announcements" className="gap-2 data-[state=active]:bg-[#E7F3FF] data-[state=active]:text-[#1877F2]">
            <Megaphone size={16} />
            Thông báo hệ thống
            {announcements.length > 0 && (
              <Badge className="ml-2 bg-[#1877F2] text-white hover:bg-[#166FE5] rounded-full px-1.5 min-w-[20px] h-5 flex items-center justify-center text-[10px] border-none">
                {announcements.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="logs" className="gap-2 data-[state=active]:bg-[#E7F3FF] data-[state=active]:text-[#1877F2]">
            <Clock size={16} />
            Nhật ký tự động
          </TabsTrigger>
        </TabsList>

        <TabsContent value="announcements" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Megaphone className="text-[#1877F2]" />
                Thông báo từ Admin
              </CardTitle>
            </CardHeader>
            <CardContent>
              {announcements.length > 0 ? (
                <div className="space-y-4">
                  {announcements.map((ann) => (
                    <div key={ann.id} className="p-4 bg-[#F7F8FA] border border-[#E4E6EB] rounded-xl space-y-2 hover:border-[#1877F2] transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-[#1C1E21] text-base">{ann.title}</h3>
                          {ann.is_popup && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 border-none text-[10px]">Nổi bật</Badge>}
                        </div>
                        <span className="text-xs text-[#8C939D]">
                          {new Date(ann.created_at).toLocaleString('vi-VN')}
                        </span>
                      </div>
                      <p className="text-sm text-[#606770] leading-relaxed whitespace-pre-wrap">{ann.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state py-8">
                  <Megaphone size={48} className="empty-state-icon mx-auto opacity-50" />
                  <h3 className="empty-state-title">Chưa có thông báo hệ thống</h3>
                  <p className="empty-state-text">Hiện tại không có thông báo nào từ hệ thống.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="stat-card">
              <div className="stat-icon primary">
                <Bell size={24} />
              </div>
              <div>
                <div className="stat-value">{logs.length}</div>
                <div className="stat-label">Tổng thông báo</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon success">
                <Mail size={24} />
              </div>
              <div>
                <div className="stat-value">{logs.filter(l => l.channel === 'email').length}</div>
                <div className="stat-label">Qua Email</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon warning">
                <MessageSquare size={24} />
              </div>
              <div>
                <div className="stat-value">{logs.filter(l => l.channel === 'telegram').length}</div>
                <div className="stat-label">Qua Telegram</div>
              </div>
            </div>
          </div>

          {/* Logs List */}
          <Card data-testid="notification-logs">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Chi tiết thông báo tự động</CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length > 0 ? (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div 
                      key={log.id}
                      className="flex items-start gap-4 p-4 bg-[#F7F8FA] rounded-xl hover:bg-[#F0F2F5] transition-colors"
                      data-testid={`log-item-${log.id}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-[#E7F3FF] flex items-center justify-center flex-shrink-0">
                        {getChannelIcon(log.channel)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-medium text-[#1C1E21]">{log.message}</p>
                            <p className="text-sm text-[#606770] mt-1 capitalize">
                              Kênh: {log.channel === 'email' ? 'Email' : 'Telegram'}
                            </p>
                          </div>
                          {getStatusBadge(log.status)}
                        </div>
                        <p className="text-xs text-[#8C939D] mt-2">
                          {new Date(log.sent_at).toLocaleString('vi-VN')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state py-8">
                  <Bell size={48} className="empty-state-icon mx-auto opacity-50" />
                  <h3 className="empty-state-title">Chưa có thông báo</h3>
                  <p className="empty-state-text">
                    Gửi thông báo từ Hotface List để xem lịch sử ở đây
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
